<template>
  <b-modal
    ref="partnerReferralForm"
    size="xl"
    scrollable
    hide-footer
    :title="form._id ? 'Editar parceiro' : 'Cadastrar parceiro'"
    header-bg-variant="info"
    header-text-variant="light"
  >
    <div class="partner-referral-form modal-font-family">
      <div class="mb-3">
        <label class="label">Logo do parceiro</label>
        <div class="partner-referral-form__logo">
          <div class="partner-referral-form__logo-preview">
            <img v-if="form.logoUrl" :src="form.logoUrl" alt="Logo" />
            <span v-else>Sem logo</span>
          </div>
          <div>
            <b-form-file
              accept="image/*"
              :disabled="uploadingLogo"
              browse-text="Selecionar"
              placeholder="Selecione uma imagem"
              drop-placeholder="Solte a imagem aqui"
              @change="onSelectLogo"
            />
            <small v-if="uploadingLogo" class="text-primary">Enviando imagem...</small>
          </div>
        </div>
      </div>

      <div class="mb-3">
        <label class="label">Nome</label>
        <b-form-input
          v-model="v$.form.name.$model"
          class="form-partner-referral"
          placeholder="Digite o nome do parceiro"
        />
      </div>

      <div class="mb-3">
        <label class="label">Descrição</label>
        <b-form-textarea
          v-model="v$.form.description.$model"
          class="form-partner-referral"
          rows="3"
          max-rows="5"
          placeholder="Descreva o parceiro"
        />
      </div>

      <div class="mb-3">
        <label class="label">Rótulo do benefício</label>
        <b-form-input
          v-model="form.benefitLabel"
          class="form-partner-referral"
          placeholder="Ex.: Benefício parceiro"
        />
      </div>

      <b-row>
        <b-col cols="12" md="6">
          <div class="mb-3">
            <label class="label">Condição do benefício</label>
            <b-form-input
              v-model="v$.form.benefitCondition.$model"
              class="form-partner-referral"
              placeholder="Ex.: Condição especial para assinantes"
            />
          </div>
        </b-col>

        <b-col cols="12" md="6">
          <div class="mb-3">
            <label class="label">Telefone para resgate (WhatsApp)</label>
            <b-form-input
              v-model="form.redeemPhone"
              v-mask="['(##) ####-####', '(##) #####-####']"
              class="form-partner-referral"
              placeholder="(11) 99999-9999"
            />
          </div>
        </b-col>
      </b-row>

      <div class="mb-2">
        <b-form-checkbox v-model="form.isActive" switch>
          Ativo (visível no clube)
        </b-form-checkbox>
      </div>

      <div class="d-flex mt-3">
        <base-button class="mr-1 w-50" danger block @click="close()">
          Cancelar
        </base-button>

        <b-overlay
          :show="saving"
          rounded
          opacity="0.6"
          spinner-small
          spinner-variant="primary"
          class="d-inline-block w-50"
        >
          <base-button
            success
            block
            :disabled="v$.$invalid || !form.logoUrl || uploadingLogo"
            @click="submitPartnerReferral()"
          >
            {{ form._id ? 'Atualizar' : 'Cadastrar' }}
          </base-button>
        </b-overlay>
      </div>
    </div>
  </b-modal>
</template>

<script>
import { mapActions } from 'vuex'
import useVuelidate from '@vuelidate/core'
import { required } from '@/utils/validators'
import { upload } from '@/utils/upload'
import { mask } from 'vue-the-mask'

const emptyPartnerReferral = () => ({
  name: '',
  description: '',
  logoUrl: '',
  benefitLabel: 'Benefício parceiro',
  benefitCondition: '',
  redeemPhone: '',
  isActive: true,
  _id: ''
})

export default {
  name: 'PartnerReferralFormModal',
  setup() {
    return { v$: useVuelidate() }
  },
  directives: {
    mask
  },
  data() {
    return {
      saving: false,
      uploadingLogo: false,
      form: emptyPartnerReferral()
    }
  },
  validations() {
    return {
      form: {
        name: { required },
        description: { required },
        benefitCondition: { required }
      }
    }
  },
  methods: {
    ...mapActions('partnerReferral', {
      handleCreatePartnerReferral: 'handleCreatePartnerReferral',
      handleUpdatePartnerReferral: 'handleUpdatePartnerReferral',
      setPartnerReferral: 'setPartnerReferral'
    }),
    notify(message, variant) {
      this.$bvToast.toast(message, {
        title: 'Aviso',
        variant,
        toaster: 'b-toaster-top-center',
        autoHideDelay: 5000
      })
    },
    show(partnerReferral) {
      this.form = partnerReferral ? { ...emptyPartnerReferral(), ...partnerReferral } : emptyPartnerReferral()
      this.v$.$reset()
      this.$refs.partnerReferralForm.show()
    },
    close() {
      this.$refs.partnerReferralForm.hide()
    },
    async onSelectLogo(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) {
        return
      }
      try {
        this.uploadingLogo = true
        const url = await upload(file)
        if (url && typeof url === 'string') {
          this.form.logoUrl = url
        } else {
          this.notify('Não foi possível enviar a imagem, tente novamente.', 'danger')
        }
      } catch (e) {
        this.notify('Não foi possível enviar a imagem, tente novamente.', 'danger')
      } finally {
        this.uploadingLogo = false
      }
    },
    async submitPartnerReferral() {
      try {
        this.saving = true
        const payload = {
          ...this.form,
          redeemPhone: (this.form.redeemPhone || '').replace(/\D/g, '')
        }
        await this.setPartnerReferral(payload)
        const response = this.form._id
          ? await this.handleUpdatePartnerReferral(this.form._id)
          : await this.handleCreatePartnerReferral()

        if (response && response.message) {
          this.notify(response.message, 'success')
          this.close()
          this.$emit('saved')
        } else {
          this.notify((response && response.errors) || 'Não foi possível salvar o parceiro.', 'danger')
        }
      } catch (e) {
        this.notify('Não foi possível salvar o parceiro.', 'danger')
      } finally {
        this.saving = false
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.partner-referral-form {
  .label {
    font-size: 15px;
    font-weight: 700;
    margin-bottom: 4px;
  }

  .form-partner-referral {
    height: auto;
    min-height: 42px;
  }

  &__logo {
    display: flex;
    align-items: center;
    gap: 16px;
  }

  &__logo-preview {
    width: 120px;
    height: 72px;
    border: 1px solid #ededed;
    border-radius: 6px;
    background: #fafcfd;
    display: flex;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    flex-shrink: 0;
    color: #99a0a8;
    font-size: 12px;

    img {
      max-width: 100%;
      max-height: 100%;
      object-fit: contain;
    }
  }
}
</style>
